import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import {
  findingsForAnswer,
  type AdvisoryAuditFinding,
  type AdvisoryAuditState,
} from './advisoryAudit';
import { AdvisoryEvidenceCallout, quoteCheckTone } from './AdvisoryEvidenceCallout';
import { AdvisorySidebarQuote } from './AdvisorySidebarQuote';
import { AdvisoryChapterLink } from './AdvisoryChapterLink';
import { AdvisorySourceHeading } from './AdvisorySourceHeading';
import { AdvisorySourceMetadata } from './AdvisorySourceMetadata';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';

import {
  groupAdvisoryAuditEvidence,
  type AdvisoryAnswerQuoteReferences,
} from './advisoryAuditEvidence';

const findingKinds = new Set([
  'absent_anchor',
  'unchecked_anchor',
  'enriched_context_span',
  'unjoined_fact',
  'origin_unknown',
  'unresolvable_citation',
  'verdict_disagreement',
  'unsourced_brief_figure',
  'truncated',
]);

export interface AdvisoryAuditPanelProps {
  audit?: AdvisoryAuditState;
  answerKey?: string;
  answerLabels: Readonly<Record<string, string>>;
  answerChapters?: Readonly<Record<string, number>>;
  onOpenAnswer: (answerKey: string) => void;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  quoteReferencesByAnswer?: AdvisoryAnswerQuoteReferences;
  onOpenSource?: (reference: AdvisoryQuoteReference, origin: HTMLElement) => void;
}

export function AdvisoryAuditPanel({
  audit,
  answerKey,
  answerLabels,
  answerChapters = {},
  onOpenAnswer,
  quoteReferences = [],
  quoteReferencesByAnswer,
  onOpenSource,
}: AdvisoryAuditPanelProps) {
  const { t } = useUntypedTranslation();
  const report = audit && 'report' in audit ? audit.report : null;
  const findings = answerKey ? findingsForAnswer(audit, answerKey) : report?.findings ?? [];
  const covered =
    !!report && (answerKey ? report.audited.includes(answerKey) : report.audited.length > 0);
  const state = audit?.status ?? 'unavailable';
  const statusKey =
    state === 'ready'
      ? findings.length > 0
        ? 'findings'
        : covered
        ? 'noFindings'
        : 'notChecked'
      : state;
  const checkedAnswers =
    state === 'ready' && report
      ? Object.keys(answerLabels).filter(
          key =>
            (!answerKey || answerKey === key) &&
            report.audited.includes(key) &&
            findingsForAnswer(audit, key).length === 0
        )
      : [];
  const { sources, answerFindings } = groupAdvisoryAuditEvidence({
    findings,
    allFindings: report?.findings,
    references: quoteReferences,
    referencesByAnswer: quoteReferencesByAnswer,
    answerKey,
  });
  const renderAnswerLink = (key: string) =>
    answerLabels[key] ? (
      <AdvisoryChapterLink
        label={answerLabels[key]}
        chapter={answerChapters[key]}
        onClick={() => onOpenAnswer(key)}
        inheritColor
      />
    ) : (
      <Typography sx={{ fontSize: 13 }}>{t('advisory:brief.audit.otherAnswer')}</Typography>
    );
  const renderFinding = (finding: AdvisoryAuditFinding, showDetail = false) => (
    <>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        {t(`advisory:brief.audit.severity.${finding.severity}`)}
      </Typography>
      <Typography sx={{ fontSize: 14, marginTop: 0.5 }}>
        {t(
          `advisory:brief.audit.finding.${
            findingKinds.has(finding.kind) ? finding.kind : 'unknown'
          }`
        )}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, marginTop: 1.5 }}>
        {t('advisory:brief.audit.affectedAnswer')}
      </Typography>
      {renderAnswerLink(finding.answerKey)}
      {showDetail && finding.detail ? (
        <Typography
          sx={{ fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: 1 }}
        >
          {finding.detail}
        </Typography>
      ) : null}
      {finding.downstream.length > 0 ? (
        <Box
          sx={theme => ({
            marginTop: 2,
            padding: 1.5,
            border: `1px solid ${theme.visualPrimitives.borderLight}`,
            borderRadius: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            color: theme.colors.inkPrimary,
          })}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {t('advisory:brief.audit.affectedChapters')}
          </Typography>
          <Typography
            sx={theme => ({ fontSize: 12, color: theme.colors.inkSecondary, marginTop: 0.5 })}
          >
            {t('advisory:brief.audit.downstream')}
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', margin: 0, marginTop: 1, padding: 0 }}>
            {finding.downstream.map(key => (
              <Box component="li" key={key}>
                {renderAnswerLink(key)}
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </>
  );
  return (
    <Box component="section" data-testid="advisory-audit-panel" data-audit-status={statusKey}>
      <Box>
        {statusKey !== 'findings' ? (
          <Typography role="status" data-audit-status={statusKey} sx={{ fontSize: 14 }}>
            {t(`advisory:brief.audit.status.${statusKey}`)}
          </Typography>
        ) : null}
        {report?.incomplete.length ? (
          <Typography sx={{ fontSize: 13, marginTop: 1 }}>
            {t('advisory:brief.audit.inputsUnavailable', { count: report.incomplete.length })}
          </Typography>
        ) : null}
        {report && report.skipped.length > 0 && !answerKey ? (
          <Typography sx={{ fontSize: 13, marginTop: 1 }}>
            {t('advisory:brief.audit.skipped', { count: report.skipped.length })}
          </Typography>
        ) : null}
        {sources.length > 0 ? (
          <Box component="section" sx={{ '&:not(:first-child)': { marginTop: 3 } }}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 500 }}>
              {t('advisory:brief.audit.sourceChecks')}
            </Typography>
            {sources.map(({ reference, findings: sourceFindings }) => (
              <AdvisoryEvidenceCallout
                key={reference.key}
                plain
                tone={
                  sourceFindings.some(finding => finding.severity !== 'notice')
                    ? 'error'
                    : quoteCheckTone(reference.quote.status)
                }
              >
                <AdvisorySourceHeading references={[reference]}>
                  <Typography
                    component="h4"
                    sx={{
                      fontSize: 16,
                      fontWeight: 500,
                      lineHeight: 1.5,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {onOpenSource ? (
                      <Button
                        onClick={event => onOpenSource(reference, event.currentTarget)}
                        sx={{
                          color: 'inherit',
                          font: 'inherit',
                          textTransform: 'none',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          alignItems: 'flex-start',
                          padding: 0,
                          minWidth: 0,
                          gap: 1,
                        }}
                      >
                        <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          {reference.source.title ?? t('advisory:brief.sources.untitled')}
                        </Box>
                        <Box component="span" aria-hidden="true" sx={{ flexShrink: 0 }}>
                          ›
                        </Box>
                      </Button>
                    ) : (
                      reference.source.title ?? t('advisory:brief.sources.untitled')
                    )}
                  </Typography>
                  <AdvisorySourceMetadata
                    source={reference.source}
                    checkStatus={reference.quote.status}
                  />
                  <AdvisorySidebarQuote>{reference.quote.text}</AdvisorySidebarQuote>
                  {sourceFindings.map((finding, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginTop: 1,
                        paddingTop: 1,
                      }}
                    >
                      {renderFinding(finding)}
                    </Box>
                  ))}
                </AdvisorySourceHeading>
              </AdvisoryEvidenceCallout>
            ))}
          </Box>
        ) : null}
        {answerFindings.length > 0 || checkedAnswers.length > 0 ? (
          <Typography component="h3" sx={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
            {t('advisory:brief.audit.answerChecks')}
          </Typography>
        ) : null}
        {answerFindings.length > 0 ? (
          <Typography sx={{ fontSize: 13, marginTop: 1 }}>
            {t('advisory:brief.audit.answerChecksHint')}
          </Typography>
        ) : null}
        {answerFindings.map((finding, index) => (
          <AdvisoryEvidenceCallout
            key={index}
            plain
            tone={finding.severity === 'notice' ? 'warning' : 'error'}
          >
            {renderFinding(finding, true)}
          </AdvisoryEvidenceCallout>
        ))}
        {checkedAnswers.map(key => (
          <AdvisoryEvidenceCallout key={key} tone="success" plain>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
              {t('advisory:brief.audit.passed')}
            </Typography>
            <Typography sx={{ fontSize: 14, marginTop: 0.5 }}>{answerLabels[key]}</Typography>
            <Typography sx={{ fontSize: 13, marginTop: 1 }}>
              {t('advisory:brief.audit.status.noFindings')}
            </Typography>
          </AdvisoryEvidenceCallout>
        ))}
      </Box>
    </Box>
  );
}
